"use client";

import { useState, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { trackEvent } from "@/lib/analytics";
import { useMembership } from "@/lib/hooks/useMembership";
import {
  storyblokEditable,
  StoryblokComponent,
  renderRichText,
} from "@storyblok/react/rsc";

import { EventHero } from "@/components/layout/HeroEvent";
import { SignupModal } from "@/components/layout/SignupModal";
import { MembershipPaymentModal } from "@/components/membership/MembershipPaymentModal";
import { EventSignupModal, type EventSignupData } from "@/components/events/EventSignupModal";
import { RichText } from "@/components/typography/RichText";
import type { MaskShapeVariant } from "@/components/illustrations/MaskShape";
import type { ListItemIconName } from "@/components/icons";
import type { EventStoryblok } from "@/component-types-sb";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import { selectOccurrence, getNextOccurrence, getAllOccurrencesWithMeta, findOccurrenceByParam } from "@/lib/utils/recurrence";
import { parseBerlinDate } from "@/lib/utils/berlinDate";
import { useMySignups } from "@/lib/hooks/useMySignups";
import { EventDatePicker } from "@/components/layout/EventDatePicker";
import { eventTypeFromContent } from "@/lib/utils/eventType";

type EventProps = {
  blok: EventStoryblok;
  storyUuid?: string;
};

const Event = ({ blok, storyUuid }: EventProps) => {
  const eventUuid = storyUuid || blok._uid;
  const [modalOpen, setModalOpen] = useState(false);
  const pathname = usePathname();
  // Wird hier clientseitig gelesen statt als Prop durchgereicht: searchParams
  // in der Server-Page macht die gesamte Route dynamisch, und nur diese
  // Komponente braucht den Wert ueberhaupt.
  const dateParam = useSearchParams().get("date") ?? undefined;
  const { isMember } = useMembership();
  const { isSignedUp, events: myEvents } = useMySignups();

  /** Reisen laufen durch dieselbe Komponente, haben aber weder Serientermine
   *  noch Anmeldung. Der Typ steht zur Laufzeit in `component`.
   *
   *  Der Cast ist noetig, solange `EventStoryblok.component` als Literal
   *  `"event"` deklariert ist — TypeScript haelt den Vergleich sonst fuer
   *  unmoeglich. Sobald `TravelStoryblok` generiert ist und der Prop-Typ zur
   *  Union wird, kann er weg. */
  const isTravel = (blok.component as string) === "travel";

  useEffect(() => {
    trackEvent("content_viewed", {
      // Bleibt "event", damit die Zeitreihe in analytics_events nicht bricht —
      // dort liegen die Event-Views seit April unter diesem Wert. Die
      // Unterscheidung kommt als eigenes Feld dazu.
      type: "event",
      content_type: blok.component,
      title: blok.title,
      slug: pathname,
    });
  }, [blok.component, blok.title, pathname]);

  // Get category (event type) - use as-is, EventTag handles unknown variants.
  // Reisen haben kein `category`; der Helper leitet "reisen" aus dem Typ ab,
  // damit Etikett, Maskenfarbe und der CTA-Text "Mehr erfahren" greifen.
  const category = eventTypeFromContent(blok);

  // Parse dates
  const isRepeating = !isTravel && !!blok.repeating_dates?.length;
  const allRepeatingDates = isRepeating
    ? (() => {
        const mainDate = blok.date;
        const repeating = blok.repeating_dates!;
        const mainAlreadyInRepeating = repeating.some((d) => d.date === mainDate);
        return mainAlreadyInRepeating
          ? repeating
          : [{ date: mainDate, _uid: "main", component: "event_date" as const }, ...repeating];
      })()
    : [];
  const date = isRepeating
    ? selectOccurrence(allRepeatingDates, dateParam)
    : parseBerlinDate(blok.date);
  const endDate = blok.end_date ? parseBerlinDate(blok.end_date) : undefined;

  // For repeating events, find the current occurrence's metadata
  // Try by dateParam first, then find by matching display date
  let currentOccurrence = isRepeating ? findOccurrenceByParam(allRepeatingDates, dateParam) : undefined;
  if (!currentOccurrence && isRepeating) {
    // No dateParam — find the occurrence whose display date matches
    currentOccurrence = allRepeatingDates.find((d) => {
      const display = parseBerlinDate(d.changed_date || d.date);
      return display.getTime() === date.getTime();
    });
  }
  const currentDateChanged = !!currentOccurrence?.changed_date;
  const currentIsCancelled = !!currentOccurrence?.is_cancelled || blok.is_cancelled === true;
  // Original date for signups (stable identity)
  const originalDate = currentOccurrence ? parseBerlinDate(currentOccurrence.date) : date;

  // For repeating events, suppress after-event content as long as a future occurrence exists.
  // Only show it when the last occurrence has passed.
  //
  // Travel stories have this field too and are deliberately NOT excluded
  // here: they are never recurring, so `hasNextOccurrence` is always false
  // for them, and the video should appear after a trip just as it does
  // after an event.
  const hasNextOccurrence = isRepeating && getNextOccurrence(allRepeatingDates) !== null;
  const playbackId = hasNextOccurrence
    ? undefined
    : blok.after_event_video_playback_id;

  // Parse image (undefined if no image provided)
  const image = blok.image?.filename
    ? {
        src: blok.image.filename,
        alt: blok.image.alt || blok.title,
      }
    : undefined;

  // Parse mask shape
  const maskShape = (blok.image_mask_shape || "four_vertical_even") as MaskShapeVariant;

  // Get CTA link from the event's link field
  const ctaLink = getUrlFromLinkObject(blok.link);

  // Map info_items from Storyblok to the format expected by EventHero
  const infoItems = (blok.info_items || []).map((item: any) => ({
    icon: item.icon as ListItemIconName,
    text: item.text as string,
  }));

  // When cta_type is not "link", it's a signup topic that opens a modal.
  //
  // Widened to string because this component renders both `event` and
  // `travel`, and each type now offers a different set of cta_type values
  // (event: event_signup | link, travel: link | warteliste-*). A story
  // converted between the two can also still carry an older value.
  const ctaType: string = blok.cta_type || "link";
  // Trips are booked through an external waitlist, never through
  // event_signups or the checkout. The travel type does not offer these
  // values; the guard catches leftovers that survived a conversion.
  const isEventSignup = !isTravel && ctaType === "event_signup";
  const isMembershipPayment =
    !isTravel &&
    ctaType === "membership_payment" &&
    process.env.NEXT_PUBLIC_CHECKOUT_ENABLED === "true";
  // `event_signup` and `membership_payment` are excluded by name rather than
  // via isEventSignup: on a trip that flag is already false, and the value
  // would otherwise fall through as a newsletter topic and open the generic
  // signup modal.
  const signupTopic =
    ctaType !== "link" &&
    ctaType !== "event_signup" &&
    ctaType !== "membership_payment"
      ? ctaType
      : undefined;
  const memberOnly = blok.member_access === "member_only";

  // Collect all future dates for recurring events, dedup by display date
  const allOccurrences = isRepeating ? getAllOccurrencesWithMeta(allRepeatingDates) : [];
  // For signup modal: DateEntry[] with original (identity) and display dates
  const allDates = allOccurrences.length > 0
    ? allOccurrences
        .filter((o) => !o.isCancelled)
        .map((o) => ({
          original: o.originalDate.toISOString(),
          display: o.displayDate.toISOString(),
        }))
        .filter((entry, i, arr) => arr.findIndex((e) => e.display === entry.display) === i)
    : undefined;

  // Extract speaker name from resolved experts (first expert's name)
  const speakerName = (blok.experts || [])
    .filter((e) => typeof e !== "string" && "content" in e)
    .map((e) => (e as any).content?.name as string | undefined)
    .find(Boolean) ?? undefined;

  const eventSignupData: EventSignupData = {
    storyblokEventUuid: eventUuid,
    eventDate: originalDate.toISOString(),
    eventDisplayDate: date.toISOString(),
    eventTitle: blok.title,
    eventSlug: pathname,
    // Rueckkehrziel nach dem Login. Bei Serienterminen mit `?date=`, damit der
    // Nutzer beim gewaehlten Termin landet statt beim ersten — `pathname`
    // allein traegt die Query nicht.
    returnUrl: dateParam ? `${pathname}?date=${dateParam}` : pathname,
    eventType: category,
    eventLink: ctaLink || undefined,
    speakerName,
    memberOnly,
    allDates,
  };

  // Check signup using original date (stable identity)
  const eventIsSignedUp = isSignedUp(eventUuid, originalDate.toISOString());

  // For non-repeating events, dateChanged isn't available from Storyblok
  // (no changed_date field). Fall back to the signup context which compares
  // the DB eventDate against the current Storyblok date.
  // For non-repeating events, match by UUID only (the original date in the DB
  // won't match content.date after a change). For repeating events, also match
  // by original date to pick the right occurrence.
  const signupEvent = myEvents.find((e) => {
    if (e.storyblokEventUuid !== eventUuid) return false;
    if (!isRepeating) return true;
    return (e.originalEventDate || e.eventDate) === originalDate.toISOString();
  });
  const effectiveDateChanged = currentDateChanged || (signupEvent?.dateChanged ?? false);

  return (
    <div {...storyblokEditable(blok)}>
      {/* Date picker for repeating events — at the very top */}
      {isRepeating && allOccurrences.length > 1 && (
        <EventDatePicker
          occurrences={allOccurrences}
          currentOriginalDate={originalDate}
          eventSlug={pathname}
          eventUuid={eventUuid}
        />
      )}

      <EventHero
        eventType={category}
        date={date}
        endDate={endDate}
        subline={blok.subline}
        title={blok.title}
        description={blok.text ? <RichText html={renderRichText(blok.text)} /> : undefined}
        image={image}
        playbackId={playbackId}
        maskShape={maskShape}
        infoItems={infoItems}
        ctaLabel={blok.cta || "Jetzt anmelden"}
        ctaLink={ctaLink || undefined}
        hideCta={blok.hide_cta}
        onCtaClick={(signupTopic || isEventSignup || isMembershipPayment) ? () => setModalOpen(true) : undefined}
        memberOnly={memberOnly}
        isMember={isMember}
        isSignedUp={eventIsSignedUp}
        dateChanged={effectiveDateChanged}
        isCancelled={currentIsCancelled}
      />

      {/* Body content section - same background as hero */}
      {blok.body && blok.body.length > 0 && (
        <section className="bg-sand-10 flex flex-col container max-w-7xl mx-auto rounded-3xl overflow-hidden">
          {blok.body.map((nestedBlok) => (
            <StoryblokComponent
              key={nestedBlok._uid}
              blok={nestedBlok}
              eventDate={date}
              eventOriginalDate={originalDate}
              eventCategory={category}
              eventUuid={eventUuid}
              eventTitle={blok.title}
              eventSlug={pathname}
              eventMemberOnly={memberOnly}
              signUpLink={ctaLink || undefined}
              signUpLabel={blok.cta || "Jetzt anmelden"}
              onSignupClick={(signupTopic || isEventSignup || isMembershipPayment) ? () => setModalOpen(true) : undefined}
            />
          ))}
        </section>
      )}

      {/* Newsletter/waitlist signup modal */}
      {signupTopic && (
        <SignupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          signupTopic={signupTopic}
        />
      )}

      {/* Event signup modal */}
      {isEventSignup && (
        <EventSignupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          event={eventSignupData}
        />
      )}

      {/* Membership payment modal */}
      {isMembershipPayment && (
        <MembershipPaymentModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          source="event"
        />
      )}
    </div>
  );
};

export default Event;
